/**
 * The offer letter.
 *
 * **The document a buyer is given when something is reserved**, and the one they keep. It states
 * the price, the plan and the schedule, and every one of those figures comes from this purchase's
 * own instalment rows — the same rows the statement adds up and the same rows a payment is
 * allocated against. Nothing on this page is retyped, and nothing on it is computed a second time
 * in the browser: the server hands down a finished letter composed by `offerLetter()` in
 * `@rentorng/core`, which refuses to produce one at all if the schedule does not add up to the
 * agreed price.
 *
 * That refusal is the point of the whole feature. A letter that disagrees with the statement is
 * worse than no letter — it is the document the buyer produces two years later and the developer
 * has to explain. So when the two disagree this screen shows the disagreement and no letter,
 * rather than a letter with a plausible wrong number on it.
 *
 * **It prints as a document.** The `printable` class puts it alone on the sheet; the rail, the top
 * bar and every control carry `no-print` and are gone — the same treatment the buyer's statement
 * gets, because it is the same job.
 */

import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fileSize } from "@/lib/format";
import { useSession } from "@/lib/session";
import { Alert, Button, Card, CardTitle, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";
import { Copyright } from "@/components/copyright";
import { AgencyMark } from "@/components/agency-mark";
import { IconContract, IconExternal } from "@/components/icons";

interface LetterResponse {
  letter: {
    reference: string;
    issuedOn: string;
    issuedOnLabel: string;
    acceptBy: string;
    acceptByLabel: string;
    subject: string;
    salutation: string;
    paragraphs: string[];
    schedule: Array<{
      sequence: number;
      label: string;
      dueDate: string;
      dueDateLabel: string;
      amount: number;
      amountLabel: string;
    }>;
    totals: {
      priceLabel: string;
      depositLabel: string;
      balanceLabel: string;
      instalments: number;
      totalLabel: string;
    };
    terms: string[];
    signatures: { developer: string; buyer: string };
    noun: { one: string; many: string };
    describes: string;
  };
  agency: { name: string; phone: string | null; email: string | null; address: string | null; logoUrl: string | null };
  buyer: { name: string; phone: string | null; email: string | null; address: string | null };
  purchase: { id: string; reference: string; status: string };
  /**
   * The signed copies somebody has attached against this sale.
   *
   * Every one of them is a file that was uploaded, and **not one of them is a signature this
   * product witnessed.** The wording that says so is `signedCopyStatement`, which comes from the
   * server rather than being written here — see the note above the panel that draws it.
   */
  signedCopies: SignedCopy[];
  signedCopyStatement: string;
  /** What to send as `kind` when attaching one. The server decides the value, not this screen. */
  signedCopyKind: string;
}

interface SignedCopy {
  id: string;
  name: string;
  kindLabel: string;
  mimeType: string | null;
  sizeBytes: number | null;
  /** When the file arrived here, which is the only moment about it this product observed. */
  createdAt: string;
  /** Null where the account that attached it has since been removed. */
  uploadedByName: string | null;
}

export function OfferLetterPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [attaching, setAttaching] = useState(false);
  const [attachProblem, setAttachProblem] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["offer-letter", id],
    queryFn: () => api.get<LetterResponse>(`/purchases/${id}/offer-letter`),
    /* Never retried. The one failure that matters here is a 409 saying the schedule and the price
       disagree, and retrying it three times produces the same answer three seconds later. */
    retry: false,
  });

  /**
   * Attaching a signed copy, through the sale's own documents endpoint.
   *
   * **There is deliberately no second upload endpoint for this.** A signed offer letter is one of
   * the papers of a sale, and the sale already has an endpoint that stores one: the same
   * `documents` row, the same organisation-scoped key, the same list of accepted formats and the
   * same check that the bytes agree with what the file claims to be. A route of its own would be a
   * second place for the size cap and the magic-number check to be got wrong, and the one that was
   * got wrong would be the one nobody looked at. The only thing this screen decides is the `kind`,
   * and the server tells it what that is.
   *
   * `api.post` sends JSON; a scan is multipart, so this goes by hand — base64 in a JSON body would
   * make an eight-megabyte scan a third bigger on the connection that is already the slow part.
   */
  async function attach(files: FileList | null) {
    const file = files?.[0];
    if (!file || !data) return;
    setAttachProblem(null);
    setAttaching(true);

    const form = new FormData();
    form.append("file", file);
    form.append("kind", data.signedCopyKind);

    try {
      const response = await fetch(`/api/v1/purchases/${id}/documents`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (response.ok) {
        /* Both lists, because the same row appears on this screen and on the sale's paperwork
           card, and one of them showing a file the other does not is how somebody concludes it
           did not upload and does it again. */
        await client.invalidateQueries({ queryKey: ["offer-letter", id] });
        await client.invalidateQueries({ queryKey: ["purchase-documents", id] });
      } else {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        setAttachProblem(payload?.error ?? "That copy did not upload.");
      }
    } catch {
      setAttachProblem("Could not reach the server. Check your connection and try again.");
    }

    setAttaching(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  if (isLoading) return <Spinner />;

  /*
    The refusal, shown as itself.

    A 409 from this endpoint is not a fault — it is the product declining to print a document that
    contradicts the ledger, and the message names the two figures that disagree. Showing it as a
    generic error would send somebody to look for a bug instead of at the plan.
  */
  if (error || !data) {
    return (
      <div className="max-w-3xl">
        <PageHeader
          title="Offer letter"
          back={{ to: `/app/development/sales/${id}`, label: "Sale" }}
          description="The letter issued to a buyer when a purchase is agreed."
        />
        <Alert tone="danger" title="This letter cannot be produced.">
          {(error as { message?: string } | null)?.message ??
            "The purchase could not be read. Open it and check its payment plan."}
        </Alert>
        <div className="mt-4">
          <Button variant="secondary" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const { letter, agency, buyer } = data;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Offer letter"
        back={{ to: `/app/development/sales/${id}`, label: "Sale" }}
        description="Produced from this purchase's own schedule, so it states exactly what the statement states. Download it, sign it by hand, and attach the signed copy below."
        actions={
          <span className="flex flex-wrap gap-2">
            {/*
              A real file, not an instruction to print one.

              The same document, drawn by `offerLetterPdf` from the very payload this screen is
              rendering — the server re-dispatches to this endpoint and renders what comes back, so
              the file and the page cannot state different figures. `?inline` opens it in a tab; the
              plain address saves it.
            */}
            <a
              className="btn btn-secondary no-print"
              href={`/api/v1/documents/offer-letter/${id}.pdf?inline`}
              target="_blank"
              rel="noreferrer"
            >
              Open PDF
            </a>
            <a className="btn btn-secondary no-print" href={`/api/v1/documents/offer-letter/${id}.pdf`}>
              Download
            </a>
            <Button variant="secondary" className="no-print" onClick={() => window.print()}>
              Print
            </Button>
          </span>
        }
      />

      <Card padded={false} className="printable">
        {/* The letterhead. Nothing changes for a developer who has not uploaded a mark. */}
        <div className="border-b border-ink-200 p-6">
          <div className="flex flex-wrap items-start gap-4">
            <AgencyMark name={agency.name} logoUrl={agency.logoUrl}>
              {agency.address ? <p className="text-sm text-ink-500">{agency.address}</p> : null}
              <p className="text-sm text-ink-500">{[agency.phone, agency.email].filter(Boolean).join(" · ")}</p>
            </AgencyMark>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Offer letter</p>
              <p className="font-medium">{letter.reference}</p>
              <p className="text-sm text-ink-500">{letter.issuedOnLabel}</p>
            </div>
          </div>
        </div>

        {/* Who it is addressed to. A letter with no addressee is a template. */}
        <div className="border-b border-ink-200 p-6">
          <p className="font-medium">{buyer.name}</p>
          {buyer.address ? <p className="text-sm text-ink-500">{buyer.address}</p> : null}
          <p className="text-sm text-ink-500">{[buyer.phone, buyer.email].filter(Boolean).join(" · ")}</p>

          <p className="mt-6 font-semibold">{letter.subject}</p>
          <p className="mt-4">{letter.salutation}</p>

          <div className="mt-3 space-y-3 text-sm leading-relaxed text-ink-700">
            {letter.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>

        {/* The three figures somebody checks before reading anything else. */}
        <div className="grid gap-4 border-b border-ink-200 p-6 sm:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Purchase price</p>
            <p className="mt-1 text-xl font-semibold">{letter.totals.priceLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Payable on acceptance</p>
            <p className="mt-1 text-xl font-semibold">{letter.totals.depositLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Balance</p>
            <p className="mt-1 text-xl font-semibold">{letter.totals.balanceLabel}</p>
            <p className="text-xs text-ink-500">
              over {letter.totals.instalments} {letter.totals.instalments === 1 ? "payment" : "payments"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Open until</p>
            {/* Written out. The product never shows an ambiguous numeric date, and this one is a
                deadline on a document somebody signs. */}
            <p className="mt-1 text-xl font-semibold">{letter.acceptByLabel}</p>
            <p className="text-xs text-ink-500">when the first payment falls due</p>
          </div>
        </div>

        <div className="border-b border-ink-200 px-6 pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Schedule of payments</p>
        </div>
        <Table
          head={
            <tr>
              <th>Payment</th>
              <th>Due</th>
              <th className="num">Amount</th>
            </tr>
          }
        >
          {letter.schedule.map((row) => (
            <tr key={row.sequence}>
              <td className="font-medium">{row.label}</td>
              <td className="text-ink-600">{row.dueDateLabel}</td>
              <td className="num">{row.amountLabel}</td>
            </tr>
          ))}
          {/*
            The total, on the letter.

            It is here because a schedule a buyer cannot add up is a schedule a buyer does not
            trust — and because the server refuses to produce this document at all unless this
            figure is the purchase price to the last minor unit.
          */}
          <tr>
            <td className="font-semibold" colSpan={2}>
              Total
            </td>
            <td className="num font-semibold">{letter.totals.totalLabel}</td>
          </tr>
        </Table>

        <div className="border-t border-ink-200 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Conditions</p>
          <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink-700">
            {letter.terms.map((term) => (
              <li key={term}>{term}</li>
            ))}
          </ol>
        </div>

        {/* Two signature blocks, because an offer accepted by one party is not accepted. */}
        <div className="grid gap-8 border-t border-ink-200 p-6 sm:grid-cols-2">
          <div>
            <p className="text-sm text-ink-600">{letter.signatures.developer}</p>
            <div className="mt-10 border-t border-ink-300 pt-1 text-xs text-ink-500">Signature and date</div>
          </div>
          <div>
            <p className="text-sm text-ink-600">{letter.signatures.buyer}</p>
            <div className="mt-10 border-t border-ink-300 pt-1 text-xs text-ink-500">Signature and date</div>
          </div>
        </div>

        <div className="border-t border-ink-200 p-6 text-xs text-ink-500">
          <p>
            This letter states the terms recorded against {letter.reference}. Any statement of account issued for this
            purchase is drawn from the same figures.
          </p>
          <Copyright className="mt-3" />
        </div>
      </Card>

      {/*
        What came back.

        `no-print` throughout: this is the office’s own record of a file it holds, and it has no
        business on the sheet a buyer is handed — the printed letter is the blank somebody signs.

        **The wording is the whole of the care in this panel.** An uploaded scan is a copy asserted
        to be signed. Nothing here watched anybody sign anything: there is no typed name, no
        address, no browser, no fingerprint compared — none of the six facts the letting side
        records about a signature given in the portal is available about a photograph of a page. So
        the sentence at the top comes from the server (`SIGNED_COPY_STATEMENT` in core, one place
        for one claim), the heading says “copies” rather than “signatures”, and what is listed
        against each file is only what this product actually observed — who attached it and when it
        arrived. See ADR-0162 and, for the decision this follows, ADR-0149.
      */}
      <Card className="no-print mt-6">
        <CardTitle
          action={
            can("lease.edit") ? (
              <Button variant="secondary" disabled={attaching} onClick={() => fileInput.current?.click()}>
                {attaching ? "Attaching…" : "Attach a signed copy"}
              </Button>
            ) : null
          }
          hint="More than one may be attached. The letter has two signature blocks, so a copy signed by the developer and a copy countersigned by the buyer are two separate pages — and a later copy never replaces an earlier one, because what was attached and then superseded is part of the record."
        >
          Signed copies
        </CardTitle>

        <p className="text-sm text-ink-500">{data.signedCopyStatement}</p>

        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          hidden
          onChange={(event) => attach(event.target.files)}
        />

        {attachProblem ? (
          <div className="mt-4">
            <Alert tone="danger">{attachProblem}</Alert>
          </div>
        ) : null}

        {!data.signedCopies.length ? (
          <p className="mt-4 text-sm text-ink-500">
            Nothing has come back yet. Download the letter, have it signed by hand, then attach the signed copy here.
            PDF, JPEG, PNG or WebP, up to 15MB.
          </p>
        ) : (
          <div className="mt-4">
            <Table
              head={
                <tr>
                  <th>File</th>
                  <th>Attached by</th>
                  <th>Arrived</th>
                  <th />
                </tr>
              }
            >
              {data.signedCopies.map((copy) => (
                <tr key={copy.id}>
                  <td className="font-medium">
                    <span className="flex items-center gap-2">
                      <IconContract size={16} />
                      {copy.name}
                      {copy.sizeBytes ? (
                        <span className="text-xs font-normal text-ink-400">{fileSize(copy.sizeBytes)}</span>
                      ) : null}
                    </span>
                  </td>
                  {/* The only name this record can honestly carry. It is the account that put the
                      file here — not the hand that signed the page, which nothing here saw. */}
                  <td className="text-ink-600">{copy.uploadedByName ?? "Somebody no longer on the account"}</td>
                  <td className="text-ink-600">
                    {copy.createdAt.slice(0, 10)}
                    <span className="block text-xs text-ink-400">{copy.createdAt.slice(11, 16)} UTC</span>
                  </td>
                  <td className="text-right">
                    {/* Served by the sale’s own document endpoint, which has already resolved the
                        session, the agency and the purchase. Never a guessable path, and never one
                        of ours dressed up as one of theirs: it comes back as the bytes that were
                        uploaded. */}
                    <a
                      className="btn btn-ghost btn-sm"
                      href={`/api/v1/purchases/${id}/documents/${copy.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IconExternal size={15} />
                      Open
                    </a>
                  </td>
                </tr>
              ))}
            </Table>
            <p className="mt-3 text-xs text-ink-500">
              To take one off the record, open the sale and remove it from its documents.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
